/**
 * Client telemetry emitter — TASK 7.3 Step 3c (W5).
 *
 * The single client path for exactly FIVE §11 events. Nothing else may be sent
 * from here:
 *
 *   | event                   | producer            | properties (§11)          |
 *   |-------------------------|---------------------|---------------------------|
 *   | `session_started`       | TASK 7.2 Shell.js   | client, pwa_installed     |
 *   | `search_performed`      | TASK 9.4            | query_length, result_count|
 *   | `download_completed`    | TASK 10.1           | track_id, bytes           |
 *   | `download_failed`       | TASK 10.1           | track_id, bytes, error_code|
 *   | `offline_play_attempted`| TASK 10.1           | track_id, succeeded       |
 *
 * `track_play_started/counted/failed` ride the idempotent
 * `POST /api/play-events` (TASK 6.5) because they are COUNTED; `sign_in_outcome`
 * is server-emitted (TASK 2.1). Neither is handled here.
 *
 * ── PII: DEFAULT DENY ─────────────────────────────────────────────────────
 * {@link EVENT_PROPERTIES} is an ALLOWLIST, per event. Any property not named
 * there is dropped before the event is ever queued — so a caller that
 * accidentally passes `query`, `email` or `user_id` cannot leak it, and the
 * leak cannot be reintroduced by a future caller either. Values are
 * additionally clamped: strings over {@link MAX_STRING_LENGTH} and strings
 * containing an `@` (an email shape) are dropped even from an allowlisted key.
 * `user_id` is deliberately absent from every allowlist — the server attaches
 * it from the authenticated session (§11 PII note).
 *
 * ── DELIVERY ──────────────────────────────────────────────────────────────
 * Events batch in memory and POST to `/api/telemetry` through the api-client,
 * inheriting its CSRF header + `same-origin` credentials. Offline (or after a
 * retriable failure) the batch is written to an IndexedDB buffer and flushed
 * on the `online` event.
 *
 * ⚠ **`POST /api/telemetry` DOES NOT EXIST YET — TASK 11.1 owns it.** Today
 * the first flush gets a 404 from `plugins/static-shell.ts` (which returns a
 * JSON envelope, not the SPA HTML, for unknown `/api/*`). The client then
 * PERMANENTLY DISABLES itself for the page lifetime ({@link isDisabled}) and
 * drops the queue. That is deliberate: a 404 is not retriable, and an
 * unbounded retry against a route that does not exist would be a self-inflicted
 * DoS. When 11.1 lands, the same code path starts delivering with no change
 * here. See the task report's "Missing-endpoint risk" section.
 *
 * ── SAFETY ────────────────────────────────────────────────────────────────
 * `emit` never throws into the caller (the shell calls it during mount), never
 * blocks, never awaits. Retries are bounded ({@link MAX_FLUSH_ATTEMPTS}); the
 * in-memory and persisted queues are bounded ({@link MAX_QUEUED}) and drop the
 * OLDEST events when full, so telemetry can never exhaust memory or storage.
 *
 * ── SEAM for TASK 10.1 ────────────────────────────────────────────────────
 * {@link TelemetryBuffer} is the contract for the offline queue.
 * `offline-store.ts` should export an implementation of this interface and it
 * is injected via {@link createTelemetryClient}; the local
 * {@link createIndexedDbBuffer} is the interim implementation and uses the
 * database name reserved in `lib/local-purge.ts` so a sign-out erases it.
 *
 * @module
 */

import { ApiError, apiClient, type ApiClient } from '../api/api-client.js';
import { setTelemetryEmitter } from '../modules/app/state-store.js';
import { TELEMETRY_DB_NAME } from './local-purge.js';

/** The five client-originated §11 events. */
export const TELEMETRY_EVENTS = [
  'session_started',
  'search_performed',
  'download_completed',
  'download_failed',
  'offline_play_attempted',
] as const;

export type TelemetryEventName = (typeof TELEMETRY_EVENTS)[number];

/** Per-event property ALLOWLIST (§11). Anything else is dropped. */
export const EVENT_PROPERTIES: Readonly<Record<TelemetryEventName, readonly string[]>> = {
  session_started: ['client', 'pwa_installed'],
  search_performed: ['query_length', 'result_count'],
  download_completed: ['track_id', 'bytes'],
  download_failed: ['track_id', 'bytes', 'error_code'],
  offline_play_attempted: ['track_id', 'succeeded'],
};

/** Server batch cap (TASK 11.1: > 50 events → 413). */
export const TELEMETRY_BATCH_MAX = 50;
/** Hard ceiling on queued events (memory + persisted). Oldest are dropped. */
export const MAX_QUEUED = 200;
/** Retries per batch before it is dropped. */
export const MAX_FLUSH_ATTEMPTS = 3;
/** Debounce before an idle batch is sent. */
export const FLUSH_DEBOUNCE_MS = 2_000;
/** Strings longer than this are dropped even from an allowlisted property. */
export const MAX_STRING_LENGTH = 64;

export type TelemetryValue = string | number | boolean;

/** The wire shape — exactly what `POST /api/telemetry` receives per element. */
export interface TelemetryEvent {
  readonly event: TelemetryEventName;
  /** ISO-8601 UTC. */
  readonly occurred_at: string;
  readonly props: Readonly<Record<string, TelemetryValue>>;
}

/**
 * A queued event. `attempts` is bookkeeping ONLY — it is stripped by
 * {@link toWire} and never crosses the network. It is what makes the retry
 * budget bounded across the persist/reconnect boundary: without it a batch
 * that keeps failing would be re-parked forever.
 */
export interface BufferedTelemetryEvent extends TelemetryEvent {
  readonly attempts: number;
}

/** Offline queue contract — TASK 10.1's `offline-store.ts` implements this. */
export interface TelemetryBuffer {
  append(events: readonly BufferedTelemetryEvent[]): Promise<void>;
  /** Removes and returns up to `limit` events (FIFO). */
  drain(limit: number): Promise<BufferedTelemetryEvent[]>;
}

/** Strips the local bookkeeping field. */
export function toWire(event: BufferedTelemetryEvent): TelemetryEvent {
  return { event: event.event, occurred_at: event.occurred_at, props: event.props };
}

/* ── PII-safe payload construction ─────────────────────────────────────── */

/** Recognised as an event name? Unknown names are refused outright. */
export function isTelemetryEventName(value: string): value is TelemetryEventName {
  return (TELEMETRY_EVENTS as readonly string[]).includes(value);
}

/**
 * Projects a caller payload onto the event's allowlist. Default deny: unknown
 * keys, non-primitive values, over-long strings and email-shaped strings never
 * make it into the returned object.
 */
export function sanitizeProps(
  event: TelemetryEventName,
  payload: Readonly<Record<string, unknown>>,
): Record<string, TelemetryValue> {
  const allowed = EVENT_PROPERTIES[event];
  const out: Record<string, TelemetryValue> = {};
  for (const key of allowed) {
    if (!Object.prototype.hasOwnProperty.call(payload, key)) continue;
    const value = payload[key];
    if (typeof value === 'number') {
      if (Number.isFinite(value)) out[key] = value;
      continue;
    }
    if (typeof value === 'boolean') {
      out[key] = value;
      continue;
    }
    if (typeof value === 'string') {
      // Free text and identifiers-with-@ are the two PII shapes §11 forbids.
      if (value.length > MAX_STRING_LENGTH) continue;
      if (value.includes('@')) continue;
      out[key] = value;
    }
  }
  return out;
}

/* ── IndexedDB buffer (interim; TASK 10.1 supersedes) ──────────────────── */

const IDB_STORE = 'events';
const IDB_VERSION = 1;

/**
 * Runtime shape check for a row read back out of IndexedDB.
 *
 * `IDBObjectStore.getAll()` is typed `IDBRequest<any[]>` by lib.dom, and the
 * repo bans `any`. Rather than assert the type away, rows are VALIDATED — which
 * is the right call anyway: persisted data is input, another tab (or a stale
 * schema version) could have written something else, and a malformed row must
 * not reach the wire.
 */
export function isBufferedTelemetryEvent(value: unknown): value is BufferedTelemetryEvent {
  if (typeof value !== 'object' || value === null) return false;
  const row = value as Record<string, unknown>;
  return (
    typeof row.event === 'string' &&
    isTelemetryEventName(row.event) &&
    typeof row.occurred_at === 'string' &&
    typeof row.attempts === 'number' &&
    typeof row.props === 'object' &&
    row.props !== null
  );
}

/** Promise wrapper for an IDBRequest, wired with addEventListener (CSP W3). */
function awaitRequest<T>(request: IDBRequest<T>): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    request.addEventListener('success', () => {
      resolve(request.result);
    });
    request.addEventListener('error', () => {
      reject(request.error ?? new Error('IndexedDB request failed'));
    });
  });
}

/**
 * IndexedDB-backed buffer. When the runtime has no IndexedDB (jsdom, private
 * modes, hardened browsers) every method resolves to a no-op — telemetry is
 * diagnostic and must never break the app.
 */
export function createIndexedDbBuffer(
  factory: IDBFactory | undefined = (globalThis as { indexedDB?: IDBFactory }).indexedDB,
  databaseName: string = TELEMETRY_DB_NAME,
): TelemetryBuffer {
  const unavailable = factory === undefined || factory === null;

  async function withStore<T>(
    mode: IDBTransactionMode,
    work: (store: IDBObjectStore) => Promise<T>,
  ): Promise<T | null> {
    if (unavailable) return null;
    let db: IDBDatabase;
    try {
      const open = factory.open(databaseName, IDB_VERSION);
      open.addEventListener('upgradeneeded', () => {
        const created = open.result;
        if (!created.objectStoreNames.contains(IDB_STORE)) {
          created.createObjectStore(IDB_STORE, { autoIncrement: true });
        }
      });
      db = await awaitRequest(open);
    } catch {
      return null;
    }
    try {
      return await work(db.transaction(IDB_STORE, mode).objectStore(IDB_STORE));
    } catch {
      return null;
    } finally {
      db.close();
    }
  }

  return {
    async append(events) {
      await withStore('readwrite', async (store) => {
        // Trim from the FRONT so the queue stays bounded and recent events win.
        const keys = await awaitRequest(store.getAllKeys());
        const overflow = keys.length + events.length - MAX_QUEUED;
        for (let i = 0; i < overflow && i < keys.length; i += 1) {
          const key = keys[i];
          if (key !== undefined) store.delete(key);
        }
        for (const event of events) store.add(event);
        return null;
      });
    },
    async drain(limit) {
      const drained = await withStore('readwrite', async (store) => {
        const keys = (await awaitRequest(store.getAllKeys())).slice(0, limit);
        const rows: unknown = await awaitRequest(store.getAll());
        const values: BufferedTelemetryEvent[] = [];
        if (Array.isArray(rows)) {
          for (const row of rows.slice(0, limit)) {
            if (isBufferedTelemetryEvent(row)) values.push(row);
          }
        }
        for (const key of keys) store.delete(key);
        return values;
      });
      return drained ?? [];
    },
  };
}

/* ── Client ────────────────────────────────────────────────────────────── */

/** Just the two listener methods — avoids depending on the full DOM `Window`. */
export interface EventTargetLike {
  addEventListener(type: string, listener: () => void): void;
  removeEventListener(type: string, listener: () => void): void;
}

export interface TelemetryClientDeps {
  readonly api?: Pick<ApiClient, 'sendTelemetry'>;
  readonly buffer?: TelemetryBuffer;
  readonly win?: EventTargetLike;
  readonly isOnline?: () => boolean;
  readonly now?: () => Date;
  readonly debounceMs?: number;
  readonly warn?: (message: string, detail: Record<string, string | number>) => void;
}

export interface TelemetryClient {
  /** Fire-and-forget. Never throws, never blocks, never awaits. */
  emit(event: string, payload: Readonly<Record<string, unknown>>): void;
  /** Sends everything queued (memory first, then the offline buffer). */
  flush(): Promise<void>;
  /** Registers as the store's emitter + wires the reconnect flush. Idempotent. */
  install(): () => void;
  /** Test/diagnostic view of the in-memory queue depth. */
  readonly queueDepth: number;
  /** True once a non-retriable failure (404/413/422) disabled delivery. */
  readonly isDisabled: boolean;
}

export function createTelemetryClient(deps: TelemetryClientDeps = {}): TelemetryClient {
  const {
    api = apiClient,
    buffer = createIndexedDbBuffer(),
    win = globalThis,
    // `?.` guards the runtimes that have no `navigator` at all (node tests).
    isOnline = () => globalThis.navigator?.onLine !== false,
    now = () => new Date(),
    debounceMs = FLUSH_DEBOUNCE_MS,
    warn = (message, detail) => {
      console.warn(message, detail);
    },
  } = deps;

  let queue: BufferedTelemetryEvent[] = [];
  let disabled = false;
  let installed = false;
  let timer: ReturnType<typeof setTimeout> | null = null;
  let flushing: Promise<void> | null = null;

  function schedule(): void {
    if (timer !== null) return;
    timer = setTimeout(() => {
      timer = null;
      void flush();
    }, debounceMs);
  }

  function enqueue(event: BufferedTelemetryEvent): void {
    queue.push(event);
    // Bounded: drop the OLDEST so a long offline session cannot grow without
    // limit (and cannot be used to exhaust memory).
    if (queue.length > MAX_QUEUED) queue = queue.slice(queue.length - MAX_QUEUED);
    if (queue.length >= TELEMETRY_BATCH_MAX) void flush();
    else schedule();
  }

  /** Classifies a flush failure. Only `retry` ever goes back on the queue. */
  function dispositionOf(error: unknown): 'retry' | 'drop' | 'disable' {
    if (error instanceof ApiError) {
      // 404: the route does not exist (TASK 11.1 pending) — stop for good.
      if (error.status === 404) return 'disable';
      // 413/422: this batch is structurally unacceptable — resending cannot help.
      if (error.status === 413 || error.status === 422) return 'drop';
      // 401/403: no usable session for telemetry; not worth a retry storm.
      if (error.status === 401 || error.status === 403) return 'drop';
      return 'retry';
    }
    return 'retry';
  }

  /**
   * Sends one batch. A retriable failure re-parks the batch with `attempts`
   * incremented; events that have exhausted {@link MAX_FLUSH_ATTEMPTS} are
   * dropped, so the retry loop always terminates.
   */
  async function send(batch: readonly BufferedTelemetryEvent[]): Promise<void> {
    try {
      await api.sendTelemetry(batch.map(toWire));
    } catch (error) {
      const disposition = dispositionOf(error);
      if (disposition === 'disable') {
        disabled = true;
        warn('telemetry: endpoint unavailable — delivery disabled for this session', {
          dropped: batch.length,
        });
        return;
      }
      if (disposition === 'drop') {
        warn('telemetry: batch rejected, dropped', { dropped: batch.length });
        return;
      }
      const retriable = batch
        .map((event) => ({ ...event, attempts: event.attempts + 1 }))
        .filter((event) => event.attempts < MAX_FLUSH_ATTEMPTS);
      const exhausted = batch.length - retriable.length;
      if (exhausted > 0) warn('telemetry: retry budget exhausted, dropped', { dropped: exhausted });
      if (retriable.length > 0) await buffer.append(retriable);
    }
  }

  async function flushOnce(): Promise<void> {
    if (disabled) {
      queue = [];
      return;
    }
    if (!isOnline()) {
      if (queue.length > 0) {
        const parked = queue;
        queue = [];
        await buffer.append(parked);
      }
      return;
    }

    // Persisted events first (FIFO across the reconnect boundary).
    const persisted = await buffer.drain(TELEMETRY_BATCH_MAX);
    if (persisted.length > 0) await send(persisted);
    if (disabled) {
      queue = [];
      return;
    }

    while (queue.length > 0) {
      const batch = queue.splice(0, TELEMETRY_BATCH_MAX);
      await send(batch);
      if (disabled) {
        queue = [];
        return;
      }
    }
  }

  /** Serialised: concurrent callers share one in-flight flush. */
  function flush(): Promise<void> {
    if (flushing !== null) return flushing;
    const run = flushOnce()
      .catch((error: unknown) => {
        // Belt and braces: flush must never reject into a caller.
        warn('telemetry: flush failed', {
          reason: error instanceof Error ? error.name : 'unknown',
        });
      })
      .finally(() => {
        flushing = null;
      });
    flushing = run;
    return run;
  }

  function emit(event: string, payload: Readonly<Record<string, unknown>>): void {
    try {
      if (disabled) return;
      if (!isTelemetryEventName(event)) {
        warn('telemetry: refusing unknown event', { event: String(event).slice(0, 40) });
        return;
      }
      enqueue({
        event,
        occurred_at: now().toISOString(),
        props: sanitizeProps(event, payload),
        attempts: 0,
      });
    } catch {
      /* telemetry must never break its caller */
    }
  }

  const handleOnline = (): void => {
    void flush();
  };

  return {
    emit,
    flush,

    install() {
      if (installed) return () => undefined;
      installed = true;
      // THE wiring that turns the shell's no-op emitter into a live path.
      setTelemetryEmitter({ emit });
      win.addEventListener('online', handleOnline);
      return () => {
        installed = false;
        win.removeEventListener('online', handleOnline);
        setTelemetryEmitter(null);
        if (timer !== null) {
          clearTimeout(timer);
          timer = null;
        }
      };
    },

    get queueDepth(): number {
      return queue.length;
    },

    get isDisabled(): boolean {
      return disabled;
    },
  };
}

/* ── The TASK 10.1 buffer swap ─────────────────────────────────────────── */

/**
 * The buffer the process-wide client actually writes to. A SETTER rather than
 * an import, for the same reason `state-store.js#setTelemetryEmitter` is one:
 * `lib/offline-store.ts` imports {@link MAX_QUEUED} from THIS module, so this
 * module importing the store back would be a cycle — and it would drag
 * IndexedDB into every unit test that touches telemetry.
 *
 * `modules/app/index.js` installs `offlineStore.telemetryBuffer` at boot, which
 * moves the queue into the one database TASK 10.1 owns. Until it does (and in
 * tests) {@link createIndexedDbBuffer}'s interim buffer serves — both databases
 * are named in `local-purge.ts#KNOWN_DB_NAMES`, so either one is erased by a
 * sign-out and no event can outlive its user whichever is in force.
 */
let installedBuffer: TelemetryBuffer | null = null;
let interimBuffer: TelemetryBuffer | null = null;

function activeBuffer(): TelemetryBuffer {
  if (installedBuffer !== null) return installedBuffer;
  interimBuffer ??= createIndexedDbBuffer();
  return interimBuffer;
}

/**
 * Installs (or with `null`, removes) the persistent telemetry queue.
 *
 * ── THE INTERIM QUEUE IS DRAINED INTO THE NEW ONE (QA W6) ────────────────
 * Events emitted before `startApp` installs the offline store's buffer land in
 * the interim `steamymusic-telemetry` database. Superseding it without moving
 * them would silently drop every event of the cold-start window — which is
 * precisely the window `session_started` and the boot-time `download_*` events
 * live in, i.e. the ones §11 most wants. So the interim queue is drained once
 * and appended to the incoming buffer.
 *
 * It is BEST-EFFORT and asynchronous: the swap itself is synchronous (callers
 * depend on that), and a failed migration must lose telemetry rather than break
 * the boot. Bounded by `MAX_QUEUED`, like every other drain here.
 *
 * The alternative — documenting the drop as intentional — was rejected: the
 * events are already captured, the move costs one drain, and "we threw away the
 * cold-start events on purpose" is not a defensible answer to a gap in the
 * §11 dashboards.
 *
 * @returns the buffer that was previously installed
 */
export function setTelemetryBuffer(buffer: TelemetryBuffer | null): TelemetryBuffer | null {
  const previous = installedBuffer;
  /*
   * The buffer being SUPERSEDED, whichever it is — the interim IndexedDB one
   * at boot, or a previously installed one. Read before the swap, because
   * after it `activeBuffer()` answers with the new one.
   */
  const superseded = previous ?? interimBuffer;
  installedBuffer = buffer;

  // Nothing to migrate INTO (a teardown restoring `null`), or nothing to
  // migrate FROM, or the same object: no-op.
  if (buffer === null || superseded === null || superseded === buffer) return previous;

  // Detached from the swap: the swap itself must stay synchronous, no caller
  // can meaningfully wait for this, and a rejection must not surface as a
  // boot failure.
  void superseded
    .drain(MAX_QUEUED)
    .then(async (parked) => {
      if (parked.length > 0) await buffer.append(parked);
    })
    .catch(() => {
      /* the events are gone either way; a throw here would be worse */
    });
  return previous;
}

/** Late-bound indirection, so a swap after construction is honoured. */
const lateBoundBuffer: TelemetryBuffer = {
  append: (events) => activeBuffer().append(events),
  drain: (limit) => activeBuffer().drain(limit),
};

/** The process-wide emitter; installed once from `startApp`. */
export const telemetryClient: TelemetryClient = createTelemetryClient({ buffer: lateBoundBuffer });
